<script setup lang="ts">
import { onMounted, ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import type { ImageCategory } from "@/types/images";
import type {
  Sticker,
  StickerCollectionTag,
  StickerUpdateParams,
  StickerUpload,
} from "@/types/stickers";
import CompleteStickerModal from "@/views/stickers/modals/CompleteStickerModal.vue";
import AddStickersModal from "@/views/stickers/modals/AddStickersModal.vue";
import ConfirmationModal from "../modals/ConfirmationModal.vue";
import { createEmptySticker } from "@/types/stickers";
import { edit, trash_can, plus } from "pn-design-assets/pn-assets/icons";
import { userStickers } from "@/stores/stickers";
import { useImageStore } from "@/stores/images";
import Header from "@/components/reusables/CommonHeader.vue";

const stickerStore = userStickers();
const showSpinner = ref(false);
const {
  currentCollection,
  notificationObj,
  currentCollectionStickers,
  createCollectionReqestParams,
} = storeToRefs(stickerStore);

const heading = ref("");
const goBackHref = ref("/stickers");
const goBackText = ref("Back to sticker collection");
const description = ref(
  "You can manage the collection as a whole and stickers individually. The collection becomes available to users when the “active” toggle in the upper right is turned on. Note! Stickers that have not had individual tags and alt. text added, won’t be visible to the users.",
);
const route = useRoute();
const router = useRouter();
const stickerCollectionId = route.params.id as string;
const imageStore = useImageStore();
const showCompleteStickerModal = ref(false);
const showCompleteStickerLoader = ref(false);
const showAddStickersModal = ref(false);
const showAddStickersLoader = ref(false);
const activeSticker = ref<Sticker>({ ...createEmptySticker() });
const showDeleteStickerModal = ref(false);
const selectedStickerId = ref<number | null>(null);
const activeToggleValue = ref(false);
const activeToggleKey = ref(0);

const reset = () => {
  stickerStore.reset();
};

onMounted(async () => {
  try {
    reset();
    showSpinner.value = true;
    await stickerStore.getStickerCollectionById(Number(stickerCollectionId));
    await stickerStore.getStickersForCollection(Number(stickerCollectionId));
    heading.value = `The ${currentCollection.value.enname} collection`;
  } catch {
    stickerStore.populateNotificationObj({
      show: true,
      message: "There was an error in fetching collection details",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } finally {
    showSpinner.value = false;
  }
});

watch(
  () => currentCollection.value.active,
  (active) => {
    activeToggleValue.value = active;
  },
  { immediate: true },
);

const openCompleteStickerModal = (sticker: Sticker) => {
  showCompleteStickerModal.value = true;
  activeSticker.value = sticker;
};

const closeCompleteStickerModal = () => {
  showCompleteStickerModal.value = false;
};

const openAddStickersModal = () => {
  showAddStickersModal.value = true;
};

const closeAddStickersModal = () => {
  showAddStickersModal.value = false;
};

const addStickersToCollection = async (stickers: StickerUpload[]) => {
  if (stickers.length === 0) {
    return;
  }

  try {
    stickerStore.resetNotification();
    showAddStickersLoader.value = true;
    await stickerStore.appendStickersToCollection(
      Number(stickerCollectionId),
      stickers,
    );
    await stickerStore.getStickersForCollection(Number(stickerCollectionId));
    stickerStore.populateNotificationObj({
      show: true,
      message: "Stickers added successfully",
      status: "success",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
    window.scrollTo(0, 0);
  } catch {
    stickerStore.populateNotificationObj({
      show: true,
      message: "There was an error adding stickers",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } finally {
    showAddStickersLoader.value = false;
    showAddStickersModal.value = false;
  }
};

const completeSticker = async (payload: StickerUpdateParams) => {
  try {
    stickerStore.resetNotification();
    showCompleteStickerLoader.value = true;
    await stickerStore.updateSticker(payload);
    await stickerStore.getStickersForCollection(Number(stickerCollectionId));
    showCompleteStickerModal.value = false;
    stickerStore.populateNotificationObj({
      show: true,
      message: "Sticker updated successfully",
      status: "success",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
    window.scrollTo(0, 0);
  } catch {
    stickerStore.populateNotificationObj({
      show: true,
      message: "There was an error updating sticker",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } finally {
    showCompleteStickerLoader.value = false;
  }
};

const categoryNames = computed(() => {
  return (stickerStore.currentCollection?.categories ?? [])
    .map(
      (catId: number) =>
        imageStore.categories.find((c: ImageCategory) => c.categoryId === catId)
          ?.english || "",
    )
    .filter(Boolean) // remove empty strings
    .join(", ");
});

const englishTags = computed<string[]>(() => {
  const tags = stickerStore.currentCollection?.tags ?? [];
  const englishTagObj = tags.find(
    (t: StickerCollectionTag) => t.language === "english",
  );
  const collectionTags = englishTagObj?.tags ?? [];
  const categoryTags =
    stickerStore.currentCollection?.resolvedCategoryTags?.english ?? [];
  return [...collectionTags, ...categoryTags];
});

const swedishTags = computed<string[]>(() => {
  const tags = stickerStore.currentCollection?.tags ?? [];
  const swedishTagObj = tags.find(
    (t: StickerCollectionTag) => t.language === "swedish",
  );
  const collectionTags = swedishTagObj?.tags ?? [];
  const categoryTags =
    stickerStore.currentCollection?.resolvedCategoryTags?.swedish ?? [];
  return [...collectionTags, ...categoryTags];
});

const readyStickerCount = computed(() => {
  const stickers = currentCollectionStickers.value ?? [];
  const completedStickers = stickers.filter((sticker) => {
    return sticker.completed === true;
  });
  return completedStickers.length;
});

const incompleteStickersCount = computed(() => {
  const stickers = currentCollectionStickers.value ?? [];
  const completedStickers = stickers.filter((sticker) => {
    return sticker.completed === false;
  });
  return completedStickers.length;
});

const toggleEvent = async (event: any) => {
  const previousActive = activeToggleValue.value;
  const nextActive = event?.target?.checked ?? !previousActive;

  // Validate that if activating, there must be at least one ready sticker
  if (nextActive && readyStickerCount.value === 0) {
    activeToggleValue.value = previousActive;
    activeToggleKey.value += 1;
    stickerStore.populateNotificationObj({
      show: true,
      message:
        "You need to have at least one sticker in ready state to activate the collection",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
    return;
  }

  activeToggleValue.value = nextActive;

  try {
    stickerStore.resetNotification();
    showSpinner.value = true;
    createCollectionReqestParams.value.active = nextActive;
    createCollectionReqestParams.value.categories =
      currentCollection.value.categories;
    createCollectionReqestParams.value.enName = currentCollection.value.enname;
    createCollectionReqestParams.value.svName = currentCollection.value.svname;
    createCollectionReqestParams.value.languages =
      currentCollection.value.languages;
    createCollectionReqestParams.value.enTags = englishTags.value;
    createCollectionReqestParams.value.svTags = swedishTags.value;
    await stickerStore.updateStickerCollection();
    if (activeToggleValue.value) {
      stickerStore.populateNotificationObj({
        show: true,
        message: `Collection activated. ${readyStickerCount.value} sticker available to users`,
        status: "success",
      });
    } else {
      stickerStore.populateNotificationObj({
        show: true,
        message: "Sticker inactivated. Stickers not available to users",
        status: "success",
      });
    }
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } catch {
    activeToggleValue.value = previousActive;
    activeToggleKey.value += 1;
    stickerStore.populateNotificationObj({
      show: true,
      message: "There was an error updating collection activation",
      status: "error",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } finally {
    showSpinner.value = false;
  }
};

const getStickerUpdateLable = (isStickerCompleted: boolean) => {
  return isStickerCompleted ? "Edit" : "Complete";
};

const openDeleteStickerModal = (stickerId: number) => {
  selectedStickerId.value = stickerId;
  showDeleteStickerModal.value = true;
};

const closeDeleteStickerModal = () => {
  showDeleteStickerModal.value = false;
  selectedStickerId.value = null;
};

const goToEditCollection = () => {
  router.push(`/edit-sticker-collection/${stickerCollectionId}`);
};

const confirmDeleteSticker = async () => {
  if (selectedStickerId.value === null) {
    closeDeleteStickerModal();
    return;
  }
  try {
    stickerStore.resetNotification();
    showSpinner.value = true;
    await stickerStore.deleteSticker(selectedStickerId.value);
    await stickerStore.getStickersForCollection(Number(stickerCollectionId));

    // Check if collection is active and if there are no ready stickers left
    if (currentCollection.value.active && readyStickerCount.value === 0) {
      // Deactivate the collection
      createCollectionReqestParams.value.active = false;
      createCollectionReqestParams.value.categories =
        currentCollection.value.categories;
      createCollectionReqestParams.value.enName =
        currentCollection.value.enname;
      createCollectionReqestParams.value.svName =
        currentCollection.value.svname;
      createCollectionReqestParams.value.languages =
        currentCollection.value.languages;
      createCollectionReqestParams.value.enTags = englishTags.value;
      createCollectionReqestParams.value.svTags = swedishTags.value;
      await stickerStore.updateStickerCollection();
      activeToggleValue.value = false;
      activeToggleKey.value += 1;
      stickerStore.populateNotificationObj({
        show: true,
        status: "warning",
        message:
          "Sticker deleted successfully. Collection has been deactivated as there are no ready stickers.",
      });
    } else {
      stickerStore.populateNotificationObj({
        show: true,
        status: "success",
        message: "Sticker deleted successfully",
      });
    }
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } catch {
    stickerStore.populateNotificationObj({
      show: true,
      status: "error",
      message: "There was an error deleting the sticker",
    });
    setTimeout(() => {
      stickerStore.resetNotification();
    }, 4000);
  } finally {
    showSpinner.value = false;
    closeDeleteStickerModal();
  }
};
</script>

<template>
  <div class="width-80">
    <pn-toast
      class="bottom-toast"
      :appearance="notificationObj.status"
      v-show="notificationObj.show"
      :text="notificationObj.message"
      temporary
    ></pn-toast>
  </div>
  <div class="flex">
    <div class="width-80">
      <Header
        :heading="heading"
        :description="description"
        :go-back-href="goBackHref"
        :go-back-text="goBackText"
        :show-stepper="false"
      ></Header>
    </div>
    <div :class="[{ spinner: showSpinner }]">
      <pn-spinner size="3" v-show="showSpinner" />
    </div>
    <div class="width-20 toggle-class">
      <pn-toggle-switch
        :key="activeToggleKey"
        label="Active"
        :checked="activeToggleValue"
        :disabled="showSpinner"
        @change="toggleEvent"
      ></pn-toggle-switch>
    </div>
  </div>
  <div class="flex flex-direction-column gap-16 collection-description">
    <div class="flex justify-space-between align-center">
      <h3 class="pn-m">Collection details</h3>
      <pn-button
        label="Edit Collection Details"
        appearance="light"
        small="true"
        left-icon="true"
        :icon="edit"
        class="margin-0"
        @pnClick="goToEditCollection"
      >
      </pn-button>
    </div>
    <div class="flex flex-direction-column">
      <h4 class="pn-s font-weight-medium margin-0">Name</h4>
      <p class="margin-0">{{ currentCollection.enname }}</p>
    </div>
    <div class="flex flex-direction-column">
      <h4 class="pn-s font-weight-medium margin-0">Categories</h4>
      <p class="margin-0">{{ categoryNames }}</p>
    </div>
    <div class="flex flex-direction-column">
      <h4 class="pn-s font-weight-medium margin-0">Tags</h4>
      <div class="flex gap-4 flex-wrap">
        <span class="pn-s">Swedish:</span>
        <pn-tag
          small
          appearance="discrete"
          :label="tag"
          class="tags"
          v-for="(tag, index) in swedishTags"
          :key="`tag-${index}`"
        ></pn-tag>
      </div>
      <br />
      <div class="flex gap-4 flex-wrap english-tags">
        English:
        <pn-tag
          small
          appearance="discrete"
          :label="tag"
          v-for="(tag, index) in englishTags"
          :key="`tag-${index}`"
        ></pn-tag>
      </div>
    </div>
    <div class="flex flex-direction-column">
      <h4 class="pn-s font-weight-medium margin-0">Stickers</h4>
      <div class="flex justify-space-between">
        <p class="margin-0">
          {{ readyStickerCount }} ready to display ({{
            incompleteStickersCount
          }}
          needing to be completed)
        </p>
        <pn-button
          label="Add Stickers"
          appearance="light"
          variant="outlined"
          ariahaspopup="dialog"
          :icon="plus"
          left-icon="true"
          @pnClick="openAddStickersModal"
        ></pn-button>
      </div>
    </div>

    <pn-toast
      v-if="incompleteStickersCount > 0"
      :heading="`${incompleteStickersCount} sitckers not ready`"
      text="Click on each sticker marked as “not ready” to complete the upload by adding tags and alternative text."
      appearance="warning"
      closable="true"
    ></pn-toast>
    <div class="stickers">
      <pn-card
        v-for="sticker in stickerStore.getOrderedCollectionStickers"
        :key="sticker._id"
        class="flex justify-center align-center"
      >
        <div class="flex flex-direction-column gap-10">
          <div
            class="thumb"
            :class="{ 'sticker-incomplete': !sticker.completed }"
          >
            <img :src="sticker.thumbUrl" />
          </div>
          <div class="flex gap-8 justify-center">
            <pn-button
              v-if="!sticker.completed"
              label="Complete"
              small="true"
              @click="openCompleteStickerModal(sticker)"
            ></pn-button>
            <pn-button
              v-if="sticker.completed"
              appearance="light"
              small="true"
              :icon="edit"
              :icon-only="true"
              variant="outlined"
              @click="openCompleteStickerModal(sticker)"
            ></pn-button>
            <pn-button
              appearance="light"
              variant="outlined"
              :icon="trash_can"
              :icon-only="true"
              small
              tooltip="Delete"
              @click="openDeleteStickerModal(sticker.stickerId)"
            ></pn-button>
          </div>
        </div>
      </pn-card>
    </div>
    <CompleteStickerModal
      :sticker="activeSticker"
      :open="showCompleteStickerModal"
      :showLoader="showCompleteStickerLoader"
      @onClose="closeCompleteStickerModal"
      @onSave="completeSticker"
    ></CompleteStickerModal>
    <AddStickersModal
      :open="showAddStickersModal"
      :showLoader="showAddStickersLoader"
      @onClose="closeAddStickersModal"
      @onSave="addStickersToCollection"
    />
    <ConfirmationModal
      :open="showDeleteStickerModal"
      :showLoader="showSpinner"
      heading="Are you sure you want to delete this sticker?"
      @onCancel="closeDeleteStickerModal"
      @onConfirm="confirmDeleteSticker"
    />
  </div>
</template>

<style lang="scss" scoped>
.toggle-class {
  margin-top: 8rem;
}

.collection-description {
  padding-left: 3rem;
  overflow: hidden;
}

.stickers {
  display: grid;
  width: 100%;
  max-width: 1064px;
  gap: 16px;
  grid-template-columns: repeat(4, 188px);
}

.thumb {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 120px;
  width: 140px;
}

.thumb img {
  width: auto;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: contain;
}

.sticker-incomplete {
  position: relative;
}

.sticker-incomplete:after {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  background: #f8f8f8;
  left: 0;
  top: 0;
  opacity: 0.5;
}

.bottom-toast {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  max-width: calc(100vw - 32px);
  text-align: center;
  z-index: 1000;
}

.english-tags {
  margin-bottom: 5px;
  margin-top: 5px;
}

.tags {
  margin-bottom: 2px;
}
</style>
